import {
  closestCorners,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import {
  Button,
  Card,
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner,
  Table,
  Modal,
} from "react-bootstrap";
import SortableItem from "./utils/Sortable";
import ActionButton from "components/common/ActionButton";
import { useFetchSocialMediaPlatforms } from "../../../../hooks/useFetchSocialMediaPlatforms";
import { useEffect, useState } from "react";
import { useFetchCategoriesGroup } from "hooks/useFetchCategoriesGroup";

const CatGroupWithSocialMediaIconWithTextAndLogo = ({
  show,
  block,
  onClose,
  onChange,
}) => {
  const {
    register,
    setValue,
    clearErrors,
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      name: "",
      logo: null,
      categoryGroups: [],
      socialMediaLinks: [],
      ...block.settings,
    },
  });

  const categoryArray = useFieldArray({
    control,
    name: "categoryGroups",
  });

  const socialArray = useFieldArray({
    control,
    name: "socialMediaLinks",
  });
  const { data: categoriesGroupData, isLoading: categoriesGroupDataLoading } =
    useFetchCategoriesGroup();

  let categoriesGroupDataOptions = categoriesGroupData?.data?.data;

  const {
    data: socialMediaPlatformsData,
    isLoading: socialMediaPlatformsDataLoading,
  } = useFetchSocialMediaPlatforms();

  let socialMediaPlatformsOptions = socialMediaPlatformsData?.data?.data;

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const submit = (data) => {
    onChange(data); // ✅ one controlled update
    onClose();
  };
  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Text/Logo + Category Groups + Social Media Icons
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="gx-2">
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Name"
                  title="Name"
                  className="py-2"
                  isInvalid={!!errors.name}
                  {...register("name")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="file"
                  placeholder="Logo"
                  title="Logo"
                  className="py-2"
                  isInvalid={!!errors.logo}
                  {...register("logo")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.logo?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <h5 className="fs-10 mb-0 text-nowrap pb-2">Category Groups</h5>

          <Row className="gx-2 px-1">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragEnd={(e) => {
                const { active, over } = e;
                if (!over || active.id === over.id) return;

                const oldIndex = categoryArray.fields.findIndex(
                  (f) => f.id === active.id,
                );
                const newIndex = categoryArray.fields.findIndex(
                  (f) => f.id === over.id,
                );
                categoryArray.move(oldIndex, newIndex);
              }}
            >
              <SortableContext
                items={categoryArray.fields.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <Table>
                  <thead className="bg-200">
                    <tr>
                      <th
                        scope="col"
                        className="w-0 fs-10 text-center"
                        style={{ width: "10px" }}
                      >
                        Order
                      </th>
                      <th scope="col" className="fs-10">
                        Options
                      </th>
                      <th
                        style={{ width: "10px", padding: "12px 15px" }}
                        scope="col"
                        className="fs-10 text-center"
                      >
                        Active
                      </th>
                      <th
                        scope="col"
                        style={{ padding: "9px", width: "10px" }}
                        className=" text-end"
                      >
                        <ActionButton
                          icon="plus"
                          variant="success"
                          title="Add"
                          className="px-1 py-0"
                          // disabled={options.length > 0}
                          onClick={() => categoryArray.append({})}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryArray.fields.map((item, index) => (
                      <SortableItem key={item.id} id={item.id}>
                        <td style={{ padding: "5px" }}>
                          <Form.Group controlId="formGroupName">
                            <Form.Select
                              aria-label="options"
                              isInvalid={
                                !!errors.categoryGroups?.[index]?.variantId
                              }
                              {...register(`categoryGroups.${index}.variantId`)}
                            >
                              <option value="">Select</option>
                              {categoriesGroupDataOptions?.map((d) => (
                                <option value={d.id}>{d.name}</option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {
                                errors.categoryGroups?.[index]?.variantId
                                  ?.message
                              }
                            </Form.Control.Feedback>
                          </Form.Group>
                        </td>
                        <td style={{ padding: "5px" }} className="text-center">
                          <Form.Check
                            type="checkbox"
                            id="defaultSwitch"
                            className="mt-1"
                            // defaultChecked={!state}
                            isInvalid={!!errors.categoryGroups?.[index]?.status}
                            {...register(`categoryGroups.${index}.status`)}
                          />
                        </td>
                        <td style={{ padding: "5px" }} className="text-center">
                          <ActionButton
                            icon="trash-alt"
                            title="Delete"
                            variant="danger"
                            className="px-1 py-0"
                            onClick={() => categoryArray.remove(index)}
                          />
                        </td>
                      </SortableItem>
                    ))}
                  </tbody>
                </Table>
              </SortableContext>
            </DndContext>
          </Row>
          <h5 className="fs-10 mb-0 text-nowrap pb-2">
            Social Media Platforms
          </h5>

          <Row className="gx-2 px-1">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragEnd={(e) => {
                const { active, over } = e;
                if (!over || active.id === over.id) return;

                const oldIndex = socialArray.fields.findIndex(
                  (f) => f.id === active.id,
                );
                const newIndex = socialArray.fields.findIndex(
                  (f) => f.id === over.id,
                );
                socialArray.move(oldIndex, newIndex);
              }}
            >
              <SortableContext
                items={socialArray.fields.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <Table>
                  <thead className="bg-200">
                    <tr>
                      <th
                        scope="col"
                        className="w-0 fs-10 text-center"
                        style={{ width: "10px" }}
                      >
                        Order
                      </th>
                      <th scope="col" className="fs-10">
                        Options
                      </th>
                      <th
                        style={{ width: "10px", padding: "12px 15px" }}
                        scope="col"
                        className="fs-10 text-center"
                      >
                        Active
                      </th>
                      <th
                        scope="col"
                        style={{ padding: "9px", width: "10px" }}
                        className=" text-end"
                      >
                        <ActionButton
                          icon="plus"
                          variant="success"
                          title="Add"
                          className="px-1 py-0"
                          // disabled={options.length > 0}
                          onClick={() => socialArray.append({})}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {socialArray.fields.map((item, index) => (
                      <SortableItem key={item.id} id={item.id}>
                        <td style={{ padding: "5px" }}>
                          <Form.Group controlId="formGroupName">
                            <Form.Select
                              aria-label="options"
                              isInvalid={
                                !!errors.socialArray?.[index]?.variantId
                              }
                              {...register(`socialArray[${index}].variantId`)}
                            >
                              <option value="">Select</option>
                              {socialMediaPlatformsOptions?.map((d) => (
                                <option value={d.id}>{d.name}</option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.socialArray?.[index]?.variantId?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </td>
                        <td style={{ padding: "5px" }} className="text-center">
                          <Form.Check
                            type="checkbox"
                            id="defaultSwitch"
                            className="mt-1"
                            // defaultChecked={!state}
                            isInvalid={!!errors.socialArray?.[index]?.status}
                            {...register(`socialArray[${index}].status`)}
                          />
                        </td>
                        <td style={{ padding: "5px" }} className="text-center">
                          <ActionButton
                            icon="trash-alt"
                            title="Delete"
                            variant="danger"
                            className="px-1 py-0"
                            onClick={() => socialArray.remove(index)}
                          />
                        </td>
                      </SortableItem>
                    ))}
                  </tbody>
                </Table>
              </SortableContext>
            </DndContext>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit(submit)}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CatGroupWithSocialMediaIconWithTextAndLogo;
